import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Carregando } from "../componentes/ui";
import Rodape from "../componentes/Rodape";
import {
  mascararCep,
  mascararCnpj,
  mascararMoeda,
  mascararTelefone,
} from "../lib/mascaras";
import {
  DECLARACOES,
  ETAPAS,
  MAX_SOCIOS,
  camposDaPessoa,
  temConteudo,
  type Campo,
} from "../lib/aberturaDeEmpresa";
import { enviarFormulario, lerEstadoDoFormulario, type EstadoDoFormulario } from "../lib/radarSimples";

// O questionário de abertura, respondido pelo CLIENTE de um aluno.
//
// ---------------------------------------------------------------------------
// É UMA ROTA PÚBLICA, E ELA NÃO É DO ALUNO
// ---------------------------------------------------------------------------
// Quem abre não tem conta na Contabilidade Facilitada, não vai ter, e não tem
// suporte para acionar. Então: sem Topo, sem menu, sem nenhuma mensagem de erro
// técnica. É a mesma decisão de `/orcamento/:token`.
//
// O que faz o papel da autenticação é o token, que o aluno gerou e mandou. Ele
// não revela nada do aluno além do rótulo que o próprio aluno escreveu para
// identificar este cliente — na origem, `anon` lia as três tabelas INTEIRAS com
// `using (true)`, ou seja qualquer pessoa com a chave pública lia os 309 links e
// as 60 respostas de todo mundo, com CPF e endereço de clientes finais dentro.
//
// ---------------------------------------------------------------------------
// O RASCUNHO FICA NO NAVEGADOR DO CLIENTE
// ---------------------------------------------------------------------------
// São até 13 etapas, e a pessoa preenche isso no celular, entre uma coisa e
// outra. Perder tudo porque a aba fechou é o modo de falha mais provável — e o
// dado ainda não é nosso para guardar: enquanto ele não envia, não há
// consentimento. `localStorage` resolve os dois: fica no aparelho dele, e some
// quando ele envia.

const CHAVE_DO_RASCUNHO = (token: string) => `cf:abertura:${token}`;

type Respostas = Record<string, unknown>;

export default function FormularioAbertura() {
  const { token = "" } = useParams();
  const [estado, setEstado] = useState<EstadoDoFormulario | { estado: "carregando" }>({
    estado: "carregando",
  });
  const [respostas, setRespostas] = useState<Respostas>({});
  const [etapa, setEtapa] = useState(0);
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);

  useEffect(() => {
    let vivo = true;
    lerEstadoDoFormulario(token).then((r) => {
      if (vivo) setEstado(r);
    });
    return () => {
      vivo = false;
    };
  }, [token]);

  // Recupera o rascunho. Envolvido em try/catch porque `localStorage` ATIRA em
  // alguns contextos (janela privada, site data bloqueado) — e um formulário que
  // não abre por causa do rascunho é pior que um sem rascunho.
  useEffect(() => {
    try {
      const guardado = localStorage.getItem(CHAVE_DO_RASCUNHO(token));
      if (guardado) setRespostas(JSON.parse(guardado) as Respostas);
    } catch {
      /* sem rascunho, e a tela funciona igual */
    }
  }, [token]);

  useEffect(() => {
    if (enviado) return;
    try {
      localStorage.setItem(CHAVE_DO_RASCUNHO(token), JSON.stringify(respostas));
    } catch {
      /* idem */
    }
  }, [respostas, token, enviado]);

  // Quantos sócios, e portanto quantas etapas. O responsável é sempre a etapa 1.
  const quantosSocios = useMemo(() => {
    const resp = (respostas.responsavel ?? {}) as Record<string, string>;
    if (resp.tera_socio !== "Sim") return 0;
    const n = Number.parseInt(resp.quantidade_socios ?? "1", 10);
    return Math.min(Math.max(Number.isFinite(n) ? n : 1, 1), MAX_SOCIOS);
  }, [respostas.responsavel]);

  const etapas = useMemo(
    () => [
      { chave: "responsavel", titulo: "Seus dados", campos: camposDaPessoa(true) },
      ...Array.from({ length: quantosSocios }, (_, i) => ({
        chave: `socio_${i}`,
        titulo: `Dados do sócio ${i + 2}`,
        campos: camposDaPessoa(false),
      })),
      ...ETAPAS,
      { chave: "declaracoes", titulo: "Declarações", campos: [] as Campo[] },
    ],
    [quantosSocios],
  );

  // Se a lista encolheu (a pessoa voltou e reduziu o número de sócios), o índice
  // não pode ficar apontando para uma etapa que não existe mais.
  const atual = Math.min(etapa, etapas.length - 1);
  useEffect(() => {
    if (etapa > etapas.length - 1) setEtapa(etapas.length - 1);
  }, [etapas.length, etapa]);

  const valorDe = (secao: string, campo: string): unknown => {
    const m = /^socio_(\d+)$/.exec(secao);
    if (m) return ((respostas.socios as Record<string, unknown>[]) ?? [])[Number(m[1])]?.[campo] ?? "";
    return ((respostas[secao] ?? {}) as Record<string, unknown>)[campo] ?? "";
  };

  const definir = (secao: string, campo: string, valor: unknown) => {
    const m = /^socio_(\d+)$/.exec(secao);
    if (m) {
      const i = Number(m[1]);
      setRespostas((r) => {
        const socios = [...((r.socios as Record<string, unknown>[]) ?? [])];
        socios[i] = { ...(socios[i] ?? {}), [campo]: valor };
        return { ...r, socios };
      });
      return;
    }
    setRespostas((r) => ({
      ...r,
      [secao]: { ...((r[secao] ?? {}) as Record<string, unknown>), [campo]: valor },
    }));
  };

  if (estado.estado === "carregando") return <Carregando />;

  if (estado.estado === "nao-encontrado" || estado.estado === "erro") {
    return (
      <Moldura>
        <h1 className="text-xl font-semibold text-titulo">Este link não está mais valendo</h1>
        <p className="mt-2 text-cinza">
          Confira se o endereço foi copiado inteiro. Se continuar assim, peça um link novo ao
          contador que enviou.
        </p>
      </Moldura>
    );
  }

  if (estado.estado === "respondido" || enviado) {
    // O rascunho some assim que o envio dá certo: ele existia para o caso de a
    // aba fechar antes, e manter dado pessoal no aparelho depois disso não serve
    // a ninguém.
    try {
      localStorage.removeItem(CHAVE_DO_RASCUNHO(token));
    } catch {
      /* nada a fazer */
    }
    return (
      <Moldura>
        <h1 className="text-xl font-semibold text-titulo">
          {enviado ? "Recebemos suas respostas" : "Este questionário já foi respondido"}
        </h1>
        <p className="mt-2 text-cinza">
          {enviado
            ? "Obrigado. O contador vai receber tudo o que você preencheu e entra em contato."
            : "As respostas já chegaram ao contador. Se precisar corrigir alguma coisa, fale direto com ele."}
        </p>
      </Moldura>
    );
  }

  const etapaAtual = etapas[atual];
  const ultima = atual === etapas.length - 1;
  const concordou = ((respostas.declaracoes ?? {}) as Record<string, unknown>).concordo === true;

  return (
    <Moldura rotulo={estado.rotulo}>
      <div className="mb-6 space-y-2">
        <div className="flex items-baseline justify-between text-sm">
          <span className="font-medium text-tinta">
            Etapa {atual + 1} de {etapas.length}
          </span>
          <span className="text-cinza">{etapaAtual.titulo}</span>
        </div>
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-chip">
          <div
            className="h-full rounded-full bg-acento transition-all"
            style={{ width: `${((atual + 1) / etapas.length) * 100}%` }}
            role="img"
            aria-label={`${atual + 1} de ${etapas.length} etapas`}
          />
        </div>
      </div>

      <section
        data-etapa={etapaAtual.chave}
        className="rounded-cf border border-borda bg-superficie p-4 sm:p-6"
      >
        <h1 className="text-lg font-semibold text-titulo">{etapaAtual.titulo}</h1>
        {"descricao" in etapaAtual && etapaAtual.descricao && (
          <p className="mt-1 text-sm text-cinza">{etapaAtual.descricao}</p>
        )}

        {etapaAtual.chave === "declaracoes" ? (
          <div className="mt-4 space-y-3">
            <ul className="space-y-1.5 rounded-cf bg-chip p-3 text-sm text-tinta">
              {DECLARACOES.map((d) => (
                <li key={d} className="flex gap-2">
                  <span aria-hidden="true">•</span>
                  <span>{d}</span>
                </li>
              ))}
            </ul>
            <label className="flex items-center gap-2.5 text-sm text-tinta">
              <input
                type="checkbox"
                data-concordo
                checked={concordou}
                onChange={(e) => definir("declaracoes", "concordo", e.target.checked)}
                className="size-4 accent-acento"
              />
              Li e concordo com as declarações acima.
            </label>
          </div>
        ) : (
          <div className="mt-4 space-y-4">
            {etapaAtual.campos
              .filter((c) => {
                if (!c.quando) return true;
                const v = valorDe(etapaAtual.chave, c.quando.campo);
                // O regime de bens vale para casado E para união estável — a
                // condição do dado é só o primeiro, e a segunda entra aqui, onde
                // a tela sabe o que perguntar.
                if (c.id === "regime_bens") return v === "Casado(a)" || v === "União estável";
                return v === c.quando.valor;
              })
              .map((c) => (
                <CampoDoFormulario
                  key={c.id}
                  campo={c}
                  secao={etapaAtual.chave}
                  valor={valorDe(etapaAtual.chave, c.id)}
                  aoMudar={definir}
                />
              ))}
          </div>
        )}
      </section>

      {aviso && (
        <p className="mt-3 rounded-cf border border-alerta-tinta/40 bg-alerta-fundo px-3 py-2 text-sm text-alerta-tinta">
          {aviso}
        </p>
      )}

      <div className="mt-5 flex items-center justify-between gap-3">
        <button
          type="button"
          data-voltar
          onClick={() => setEtapa((e) => Math.max(0, e - 1))}
          disabled={atual === 0}
          className={
            atual === 0
              ? "rounded-full border border-borda px-4 py-2 text-sm text-cinza opacity-50"
              : "rounded-full border border-borda bg-superficie px-4 py-2 text-sm font-medium text-tinta hover:border-acento/40"
          }
        >
          Voltar
        </button>

        {ultima ? (
          <button
            type="button"
            data-enviar
            disabled={enviando}
            onClick={async () => {
              setAviso(null);
              if (!concordou) {
                setAviso("Marque a concordância com as declarações para enviar.");
                return;
              }
              const corpo: Respostas = { ...respostas, schema_version: 2 };
              // Sócios só entram quando existem. Um array de nove vazios seria
              // nove fichas em branco na tela do contador.
              if (quantosSocios > 0) {
                corpo.socios = ((respostas.socios as unknown[]) ?? []).slice(0, quantosSocios);
              } else {
                delete corpo.socios;
              }
              if (!temConteudo(corpo)) {
                setAviso("Preencha ao menos uma resposta antes de enviar.");
                return;
              }
              setEnviando(true);
              const r = await enviarFormulario(token, corpo);
              setEnviando(false);
              if (r.ok) {
                setEnviado(true);
                return;
              }
              // Nada de mensagem técnica: quem lê não tem suporte para acionar.
              setAviso(
                r.motivo === "ja-respondido"
                  ? "Este questionário já havia sido enviado."
                  : "Não conseguimos enviar agora. Confira a conexão e tente de novo — o que você preencheu continua aqui.",
              );
            }}
            className={enviando ? "pill-desligada" : "pill-cta"}
          >
            {enviando ? "Enviando…" : "Enviar respostas"}
          </button>
        ) : (
          <button
            type="button"
            data-proximo
            onClick={() => setEtapa((e) => Math.min(etapas.length - 1, e + 1))}
            className="pill-cta"
          >
            Próxima etapa
          </button>
        )}
      </div>

      <p className="mt-4 text-xs text-cinza">
        O que você preencher fica guardado neste aparelho enquanto não enviar, para você poder
        continuar depois.
      </p>
    </Moldura>
  );
}

function Moldura({ children, rotulo }: { children: React.ReactNode; rotulo?: string | null }) {
  return (
    <div className="min-h-screen bg-fundo">
      <div className="mx-auto max-w-2xl px-4 py-8 sm:px-6">
        <header className="mb-6">
          <p className="text-xs uppercase tracking-[0.14em] text-cinza">
            Questionário de abertura de empresa
          </p>
          {rotulo && <p className="mt-1 text-sm text-tinta">Para: {rotulo}</p>}
        </header>
        {children}
      </div>
      <Rodape />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Um campo
// ---------------------------------------------------------------------------

function CampoDoFormulario({
  campo,
  secao,
  valor,
  aoMudar,
}: {
  campo: Campo;
  secao: string;
  valor: unknown;
  aoMudar: (secao: string, campo: string, valor: unknown) => void;
}) {
  const id = `${secao}-${campo.id}`;
  const texto = typeof valor === "string" ? valor : "";

  if (campo.tipo === "escolha") {
    return (
      <div role="group" aria-labelledby={`${id}-rotulo`}>
        <p id={`${id}-rotulo`} className="text-sm font-medium text-tinta">
          {campo.rotulo}
        </p>
        <div className="mt-2 space-y-1.5">
          {(campo.opcoes ?? []).map((o) => (
            <label key={o} className="flex items-center gap-2 text-sm text-tinta">
              <input
                type="radio"
                name={id}
                value={o}
                checked={texto === o}
                onChange={() => aoMudar(secao, campo.id, o)}
                className="size-4 accent-acento"
              />
              {o}
            </label>
          ))}
        </div>
      </div>
    );
  }

  if (campo.tipo === "multipla") {
    const marcados = Array.isArray(valor) ? (valor as string[]) : [];
    return (
      <div role="group" aria-labelledby={`${id}-rotulo`}>
        <p id={`${id}-rotulo`} className="text-sm font-medium text-tinta">
          {campo.rotulo}
        </p>
        <div className="mt-2 space-y-1.5">
          {(campo.opcoes ?? []).map((o) => (
            <label key={o} className="flex items-center gap-2 text-sm text-tinta">
              <input
                type="checkbox"
                checked={marcados.includes(o)}
                onChange={(e) =>
                  aoMudar(
                    secao,
                    campo.id,
                    e.target.checked ? [...marcados, o] : marcados.filter((x) => x !== o),
                  )
                }
                className="size-4 accent-acento"
              />
              {o}
            </label>
          ))}
        </div>
      </div>
    );
  }

  if (campo.tipo === "texto-longo") {
    return (
      <div className="space-y-1.5">
        <label htmlFor={id} className="block text-sm font-medium text-tinta">
          {campo.rotulo}
        </label>
        <textarea
          id={id}
          rows={4}
          value={texto}
          onChange={(e) => aoMudar(secao, campo.id, e.target.value)}
          className="w-full rounded-cf border border-borda bg-superficie px-3 py-2 text-tinta outline-none focus:border-acento focus:ring-2 focus:ring-acento/30"
        />
        {campo.dica && <p className="text-xs text-cinza">{campo.dica}</p>}
      </div>
    );
  }

  // As máscaras são de APRESENTAÇÃO. O texto mascarado é o que fica guardado
  // aqui, e é isso mesmo: o questionário grava um JSONB de texto, não colunas
  // tipadas — a conversão para número só existiria se houvesse coluna numérica
  // do outro lado, e o defeito de mandar "R$ 1.234,56" para `numeric` nasce
  // exatamente de fingir que existe.
  const mascara =
    campo.tipo === "telefone"
      ? mascararTelefone
      : campo.tipo === "cep"
        ? mascararCep
        : campo.tipo === "cnpj"
          ? mascararCnpj
          : campo.tipo === "moeda"
            ? (v: string) => mascararMoeda(v).texto
            : (v: string) => v;

  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="block text-sm font-medium text-tinta">
        {campo.rotulo}
      </label>
      <input
        id={id}
        inputMode={campo.tipo === "moeda" || campo.tipo === "cep" || campo.tipo === "telefone" ? "numeric" : "text"}
        value={texto}
        onChange={(e) => aoMudar(secao, campo.id, mascara(e.target.value))}
        className="w-full rounded-cf border border-borda bg-superficie px-3 py-2 text-tinta outline-none focus:border-acento focus:ring-2 focus:ring-acento/30"
      />
      {campo.dica && <p className="text-xs text-cinza">{campo.dica}</p>}
    </div>
  );
}
